import { useState } from 'react';
import { ChevronDown, ChevronUp, Phone } from 'lucide-react';
import { Link } from 'react-router';

const faqData = [
  {
    question: "Is the warranty free?",
    answer: "Yes! All qualifying treatments are automatically covered as long as maintenance guidelines are followed."
  },
  {
    question: "What if I miss a cleaning?",
    answer: "Staying current with checkups is required to keep your warranty valid. If you miss one, just contact us—we'll work with you."
  },
  {
    question: "Does the warranty cover cosmetic treatments?",
    answer: "Yes, many cosmetic treatments such as veneers and crowns are covered under our warranty."
  },
  {
    question: "Can I transfer the warranty if I move?",
    answer: "The warranty is only valid through our practice, but we're happy to provide records or guidance to your new dentist."
  }
];

export function WarrantyFaq() {
  const [open, setOpen] = useState(null);
  
  return (
    <section className="bg-gray-50 py-20 px-6">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Frequently Asked Questions</h2>
          <div className="h-1 w-24 bg-gradient-to-r from-teal-400 to-green-400 mx-auto rounded-full"></div>
          <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
            Everything you need to know about our dental warranty program.
          </p>
        </div>
        
        <div className="space-y-4">
          {faqData.map((faq, index) => (
            <div 
              key={index} 
              className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300"
            >
              <button
                onClick={() => setOpen(open === index ? null : index)}
                className={`w-full px-6 py-5 flex justify-between items-center transition-colors duration-300 ${
                  open === index 
                    ? 'bg-gradient-to-r from-teal-50 to-green-50 text-teal-700' 
                    : 'bg-white hover:bg-gray-50'
                }`}
                aria-expanded={open === index}
              >
                <span className="text-left font-medium text-gray-800">{faq.question}</span>
                <div className={`flex items-center justify-center w-6 h-6 rounded-full transition-colors duration-300 ${
                  open === index 
                    ? 'bg-gradient-to-r from-teal-400 to-green-400 text-white' 
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  {open === index ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </button>
              
              {open === index && (
                <div className="px-6 py-5 text-gray-600 bg-white border-t border-gray-100 animate-fadeIn">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* Call to Action - Integrated with the FAQ */}
        <WarrantyCta />
      </div>
    </section>
  );
}

export function WarrantyCta() {
  return (
    <section className="bg-gradient-to-r from-teal-500 to-green-500 text-white py-12 px-8 rounded-xl max-w-5xl mx-auto mt-16 shadow-xl relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-64 h-64 opacity-10">
        <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <path fill="#FFFFFF" d="M39.9,-65.7C52.8,-59.6,65.2,-50.9,72.2,-38.8C79.2,-26.8,80.8,-11.4,78.9,3C77,17.4,71.6,31.8,62.7,42.8C53.8,53.8,41.3,61.4,28.1,67.6C14.9,73.8,0.9,78.6,-14.4,79.2C-29.7,79.8,-46.2,76.2,-58.5,66.8C-70.8,57.4,-78.8,42.3,-83.5,26.2C-88.2,10.1,-89.6,-7,-84.1,-21.1C-78.6,-35.2,-66.1,-46.4,-52.5,-52.6C-38.9,-58.9,-24.2,-60.3,-10.5,-62.3C3.3,-64.3,17,-71.9,39.9,-65.7Z" transform="translate(100 100)" />
        </svg>
      </div>
      
      <div className="relative z-10 flex flex-col md:flex-row items-center justify-between">
        <div className="text-center md:text-left mb-8 md:mb-0">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Have Questions About Your Warranty?</h2>
          <p className="text-lg opacity-90 mb-0">We're here to help you feel confident and supported—every step of the way.</p>
        </div>
        
        <div className="flex space-x-4">
          <Link
            to="https://wa.me/+918401749379"
            target='_blank'
            className="inline-flex items-center bg-white text-teal-600 font-semibold px-6 py-3 rounded-lg shadow-lg hover:bg-gray-100 transition-colors duration-300"
          >
            <Phone size={18} className="mr-2" />
            Contact Our Team
          </Link>
        </div>
      </div>
      
      {/* Tooth-inspired decoration */}
      <div className="absolute bottom-0 left-0 w-16 h-16 opacity-10 transform rotate-45">
        <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M80,35 C80,25 75,15 65,10 C55,5 45,5 35,10 C25,15 20,25 20,35 C20,45 25,55 35,60 L50,95 L65,60 C75,55 80,45 80,35 Z" fill="#FFFFFF" />
          <path d="M50,30 C45,30 40,25 40,20 C40,15 45,10 50,10 C55,10 60,15 60,20 C60,25 55,30 50,30 Z" fill="#FFFFFF" />
        </svg>
      </div>
    </section>
  );
}

// Add this keyframe animation to your global CSS
// @keyframes fadeIn {
//   from { opacity: 0; transform: translateY(10px); }
//   to { opacity: 1; transform: translateY(0); }
// }
// 
// .animate-fadeIn {
//   animation: fadeIn 0.3s ease-out forwards;
// }